// frontend/src/pages/AnamnesePage.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import { formatCPF, formatPhone, validateCPF, extractPatientDataFromAnswers } from '../utils/validators';
import { confirmDiscardChanges, hasFormChanged } from '../utils/unsavedChanges';

import AddressCepField from '../components/anamnese/AddressCepField';
import { isAddressQuestion } from '../utils/anamneseFieldTypes';

import {
  Plus,
  FileText,
  Users,
  Edit3,
  Trash2,
  Eye,
  Share2,
  Calendar,
  CheckCircle,
  AlertCircle,
  Search,
  X,
  UserPlus,
  Link,
  MessageCircle
} from 'lucide-react';

// OBS: Ajustes mínimos aplicados:
// - Safeguards para .toLowerCase() e datas
// - Badge de status considera undefined como "Ativo"
// - Modais fecham ao clicar fora
// - Removido qualquer comentário de ESLint sobre 'react-hooks/exhaustive-deps'
// - Mantidas as seções e comportamentos originais

const normalizeQuestionText = (question) =>
  (question?.question_text || question?.label || question?.title || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getRegistrationFieldForQuestion = (question) => {
  const text = normalizeQuestionText(question);

  if (text.includes('cpf') && !text.includes('responsavel')) return 'cpf';
  if (
    (text === 'rg' || text.startsWith('rg ') || text.includes('registro geral')) &&
    !text.includes('responsavel')
  ) return 'rg';
  if (text.includes('data de nascimento') || text.includes('nascimento')) {
    return 'birth_date';
  }
  if (text.includes('e-mail') || text.includes('email')) return 'email';
  if (isAddressQuestion(question) || text.includes('endereco residencial')) {
    return 'address';
  }

  const excludedPhoneContext = [
    'emergencia',
    'responsavel',
    'medico',
    'acompanhante',
    'outra pessoa'
  ].some((term) => text.includes(term));

  if (
    !excludedPhoneContext &&
    (text.includes('celular') ||
      text.includes('whatsapp') ||
      text === 'telefone' ||
      text.includes('telefone para contato') ||
      text.includes('seu telefone'))
  ) {
    return 'phone';
  }

  return null;
};

const formatRegistrationValue = (field, value) => {
  if (!value) return '';
  if (field === 'cpf') return formatCPF(String(value));
  if (field === 'phone') return formatPhone(String(value));
  if (field === 'birth_date') return String(value).slice(0, 10);
  return String(value);
};

const normalizeRegistrationValue = (field, value) => {
  const raw = String(value || '').trim();
  if (field === 'cpf' || field === 'phone') return raw.replace(/\D/g, '');
  if (field === 'rg') return raw.replace(/[.\-\s]/g, '').toLowerCase();
  if (field === 'email') return raw.toLowerCase();
  if (field === 'birth_date') return raw.slice(0, 10);
  if (field === 'full_name') {
    return raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }
  if (field === 'address') {
    return raw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }
  return raw;
};

const extractRegistrationData = (template, answers) => {
  const result = {};
  (template?.questions || []).forEach((question) => {
    const field = getRegistrationFieldForQuestion(question);
    if (!field || result[field]) return;

    const answer = (answers || []).find(
      (item) => item.question_id === question.id
    );
    const value = answer?.answer;
    if (value !== undefined && value !== null && String(value).trim()) {
      result[field] = String(value).trim();
    }
  });
  return result;
};

const getResponseTriageStatus = (response) =>
  response?.triage_status || (response?.patient_id ? 'linked' : 'pending');

const getResponseAnswersList = (response) => {
  if (Array.isArray(response?.answers_resolved)) {
    return response.answers_resolved.map((item) => ({
      question_id: item.key,
      answer: item.value
    }));
  }
  if (Array.isArray(response?.answers)) return response.answers;
  if (response?.answers && typeof response.answers === 'object') {
    return Object.entries(response.answers).map(([key, value]) => ({
      question_id: key,
      answer: value
    }));
  }
  return [];
};

const AnamnesePage = () => {
  const { user } = useAuth();
  const permissions = new Set(user?.permissions || []);
  const canManage = permissions.has('anamnese.manage');
  const canSend = permissions.has('anamnese.send');
  const canSendWhatsApp = permissions.has('whatsapp.send');
  const canCreatePatient = permissions.has('patients.create');
  const { showToast } = useToast();
  const flowParams = new URLSearchParams(window.location.search);
  const flowPatientId = flowParams.get('patient_id') || '';
  const flowTemplateId = flowParams.get('template_id') || '';
  const flowPatientName = flowParams.get('patient_name') || '';
  const flowPatientCpf = flowParams.get('patient_cpf') || '';
  const flowPatientRg = flowParams.get('patient_rg') || '';
  const flowPatientEmail = flowParams.get('patient_email') || '';
  const flowPatientPhone = flowParams.get('patient_phone') || '';
  const flowPatientBirthDate = flowParams.get('patient_birth_date') || '';
  const flowPatientAddress = flowParams.get('patient_address') || '';
  const directFlowInitialized = useRef(false);
  const responsesRequestInFlight = useRef(false);

  const [activeTab, setActiveTab] = useState(flowPatientId ? 'local' : 'templates');
  const [templates, setTemplates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [knownPatients, setKnownPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para “preencher localmente”
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [localAnamneseData, setLocalAnamneseData] = useState({
    patient_name: flowPatientName,
    patient_email: flowPatientEmail,
    patient_phone: flowPatientPhone,
    patient_birth_date: flowPatientBirthDate,
    answers: []
  });

  const backendUrl =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8001'
    : '');

  const notifyPendingCountChanged = () => {
    window.dispatchEvent(new Event('anamnese:pending-count-changed'));
  };

  // Carregar templates
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/anamnese/templates`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error('Erro ao carregar templates');
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      showToast('Erro ao carregar templates de anamnese', 'error');
      setTemplates([]);
    }
  };

  // Carregar respostas
  const fetchResponses = async ({ silent = false } = {}) => {
    if (responsesRequestInFlight.current) return;
    responsesRequestInFlight.current = true;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/anamnese/responses?resolve_labels=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error('Erro ao carregar respostas');
      const data = await res.json();
      console.log('🔍 AnamnesePage - Respostas recebidas do backend:', data.length, 'respostas');
      if (data.length > 0) {
        console.log('🔍 Primeira resposta:', {
          id: data[0]?.id,
          patient_name: data[0]?.patient_name,
          has_answers_resolved: !!data[0]?.answers_resolved,
          answers_resolved_length: data[0]?.answers_resolved?.length,
          answers_resolved_first: data[0]?.answers_resolved?.[0]
        });
      }
      setResponses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      if (!silent) {
        showToast('Erro ao carregar respostas de anamnese', 'error');
        setResponses([]);
      }
    } finally {
      responsesRequestInFlight.current = false;
    }
  };

  const fetchKnownPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setKnownPatients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar pacientes para sugestões de vínculo:', err);
    }
  };

  // Load por aba (mínimo necessário)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        if (activeTab === 'templates') {
          await Promise.all([fetchTemplates(), fetchResponses(), fetchKnownPatients()]);
        } else if (activeTab === 'responses') {
          await Promise.all([fetchResponses(), fetchKnownPatients()]);
        } else if (activeTab === 'local') {
          // local precisa dos dois
          await Promise.all([fetchTemplates(), fetchResponses(), fetchKnownPatients()]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [activeTab]); // sem regras ESLint custom

  // Mantém contadores, indicadores e lista sincronizados com respostas públicas.
  useEffect(() => {
    const refreshResponses = () => {
      if (document.visibilityState === 'visible') {
        fetchResponses({ silent: true });
      }
    };

    const intervalId = window.setInterval(refreshResponses, 30000);
    document.addEventListener('visibilitychange', refreshResponses);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshResponses);
    };
  }, []); // polling válido apenas durante a permanência nesta página

  useEffect(() => {
    if (
      !flowPatientId ||
      activeTab !== 'local' ||
      loading ||
      directFlowInitialized.current
    ) {
      return;
    }

    directFlowInitialized.current = true;
    const requestedTemplate = (templates || []).find(
      (template) => template?.id === flowTemplateId && (template?.is_active ?? true)
    );
    const defaultTemplate = (templates || []).find(
      (template) => template?.is_default && (template?.is_active ?? true)
    );
    const initialTemplate = requestedTemplate || defaultTemplate;
    if (initialTemplate) {
      setSelectedTemplate(initialTemplate);
    }
  }, [activeTab, flowPatientId, flowTemplateId, loading, templates]);

  useEffect(() => {
    if (!flowPatientId || !selectedTemplate) return;

    const patientValues = {
      cpf: flowPatientCpf,
      rg: flowPatientRg,
      birth_date: flowPatientBirthDate,
      phone: flowPatientPhone,
      email: flowPatientEmail,
      address: flowPatientAddress
    };

    setLocalAnamneseData((previous) => {
      const nextAnswers = [...(previous.answers || [])];

      (selectedTemplate.questions || []).forEach((question) => {
        const field = getRegistrationFieldForQuestion(question);
        const patientValue = field ? patientValues[field] : '';
        if (!field || !patientValue) return;

        const alreadyAnswered = nextAnswers.some(
          (answer) => answer.question_id === question.id
        );
        if (alreadyAnswered) return;

        nextAnswers.push({
          question_id: question.id,
          question_text: question.question_text || question.label || '',
          answer: formatRegistrationValue(field, patientValue),
          answer_description: ''
        });
      });

      return { ...previous, answers: nextAnswers };
    });
  }, [
    flowPatientId,
    flowPatientCpf,
    flowPatientRg,
    flowPatientBirthDate,
    flowPatientPhone,
    flowPatientEmail,
    flowPatientAddress,
    selectedTemplate
  ]);

  // Remover template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja deletar este template?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/anamnese/templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao deletar template');
      showToast('Template deletado com sucesso', 'success');
      fetchTemplates();
    } catch (err) {
      console.error(err);
      showToast('Erro ao deletar template', 'error');
    }
  };

  const handleSetDefaultTemplate = async (template) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${backendUrl}/api/anamnese/templates/${template.id}/default`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erro ao definir template padrão');
      }
      showToast('Template definido como Anamnese padrão', 'success');
      await fetchTemplates();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao definir template padrão', 'error');
    }
  };

  // Remover resposta
  const handleDeleteResponse = async (responseId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta resposta?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/anamnese/responses/${responseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao deletar resposta');
      showToast('Resposta deletada com sucesso', 'success');
      await fetchResponses();
      notifyPendingCountChanged();
    } catch (err) {
      console.error(err);
      showToast('Erro ao deletar resposta', 'error');
    }
  };

  // Criar novo paciente a partir da resposta
  const handleCreatePatient = async (response) => {
    try {
      const token = localStorage.getItem('token');
      
      // Busca o template para extrair dados
      const template = templates.find(t => t.id === response.template_id);
      
      // Prepara answers no formato esperado pela função de extração
      let answersList = [];
      if (response.answers_resolved && Array.isArray(response.answers_resolved)) {
        // Se tem answers_resolved, converte para o formato esperado
        answersList = response.answers_resolved.map(item => ({
          question_id: item.key,
          answer: item.value
        }));
      } else if (response.answers) {
        // Se tem answers em formato dict
        if (typeof response.answers === 'object' && !Array.isArray(response.answers)) {
          answersList = Object.entries(response.answers).map(([key, value]) => ({
            question_id: key,
            answer: value
          }));
        } else if (Array.isArray(response.answers)) {
          answersList = response.answers;
        }
      }
      
      console.log('🔍 Template encontrado:', template);
      console.log('🔍 Answers para extração:', answersList);
      
      // Extrai dados automaticamente com validação de CPF, telefone, email, endereço
      const extractedData = {
        ...extractPatientDataFromAnswers(template?.questions || [], answersList),
        ...extractRegistrationData(template, answersList)
      };
      
      console.log('🔍 Dados extraídos:', extractedData);
      
      // Cria o paciente com schema correto incluindo CPF e endereço
      const patientData = {
        full_name: response.patient_name || extractedData.full_name,
        birth_date: extractedData.birth_date || '',
        email: extractedData.email || '',
        phone: extractedData.phone || '',
        cpf: extractedData.cpf || '',
        rg: extractedData.rg || '',
        address: extractedData.address || ''
      };

      // Evita duplicidade antes da criação e oferece vínculo ao cadastro existente.
      const patientsRes = await fetch(`${backendUrl}/api/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const patients = patientsRes.ok ? await patientsRes.json() : [];
      const cpfDigits = normalizeRegistrationValue('cpf', patientData.cpf);
      const emailNormalized = normalizeRegistrationValue('email', patientData.email);
      const nameNormalized = String(patientData.full_name || '').trim().toLowerCase();
      const birthDate = normalizeRegistrationValue('birth_date', patientData.birth_date);
      const existingPatient = (Array.isArray(patients) ? patients : []).find((patient) => {
        const sameCpf =
          cpfDigits &&
          normalizeRegistrationValue('cpf', patient?.cpf) === cpfDigits;
        const sameEmail =
          emailNormalized &&
          normalizeRegistrationValue('email', patient?.email) === emailNormalized;
        const sameName =
          nameNormalized &&
          String(patient?.full_name || patient?.name || '').trim().toLowerCase() === nameNormalized;
        const sameBirthDate =
          birthDate &&
          normalizeRegistrationValue('birth_date', patient?.birth_date) === birthDate;
        return sameCpf || (sameEmail && (sameName || sameBirthDate));
      });

      if (existingPatient) {
        const attach = window.confirm(
          `Já existe o paciente "${existingPatient.full_name || existingPatient.name}" com dados semelhantes. Deseja anexar esta anamnese ao cadastro existente?`
        );
        if (!attach) return;
        await attachResponseToSuggestedPatient(response, existingPatient);
        return;
      }

      if (!window.confirm(
        `Nenhum cadastro semelhante foi encontrado. Criar novo paciente com base na anamnese de "${response.patient_name}"?`
      )) return;

      console.log('🔍 Dados do paciente a serem enviados:', patientData);
      
      const res = await fetch(`${backendUrl}/api/patients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(patientData)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Erro ao criar paciente');
      }
      
      const newPatient = await res.json();
      
      // Atualiza a resposta com o patient_id
      const updateRes = await fetch(`${backendUrl}/api/anamnese/responses/${response.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: newPatient.id,
          template_id: response.template_id,
          answers: response.answers
        })
      });
      
      if (!updateRes.ok) throw new Error('Erro ao vincular anamnese');
      
      showToast('Paciente criado e anamnese vinculada com sucesso!', 'success');
      await fetchResponses();
      notifyPendingCountChanged();
      
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao criar paciente', 'error');
    }
  };

  // Anexar resposta a um paciente existente
  const handleAttachToPatient = (response) => {
    setSelectedResponse(response);
    setShowAttachModal(true);
  };

  const getRegistrationDataForResponse = (response) => {
    const template = templates.find((item) => item.id === response.template_id);
    return {
      full_name: response.patient_name || '',
      ...extractRegistrationData(template, getResponseAnswersList(response))
    };
  };

  const getSuggestedPatientForResponse = (response) => {
    const registration = getRegistrationDataForResponse(response);
    const cpf = normalizeRegistrationValue('cpf', registration.cpf);
    const email = normalizeRegistrationValue('email', registration.email);
    const fullName = normalizeRegistrationValue('full_name', registration.full_name);
    const birthDate = normalizeRegistrationValue('birth_date', registration.birth_date);

    const strongMatch = knownPatients.find((patient) => {
      const sameCpf = cpf && normalizeRegistrationValue('cpf', patient?.cpf) === cpf;
      const sameEmail = email && normalizeRegistrationValue('email', patient?.email) === email;
      const sameName = fullName && normalizeRegistrationValue(
        'full_name', patient?.full_name || patient?.name
      ) === fullName;
      const sameBirthDate = birthDate && normalizeRegistrationValue(
        'birth_date', patient?.birth_date
      ) === birthDate;
      return sameCpf || (sameName && sameBirthDate) || (sameEmail && (sameName || sameBirthDate));
    });
    if (strongMatch) return strongMatch;

    // Nome isolado é uma pista mais fraca: só sugerimos quando há um único
    // cadastro com correspondência exata, evitando escolher entre homônimos.
    const exactNameMatches = knownPatients.filter((patient) =>
      fullName && normalizeRegistrationValue(
        'full_name', patient?.full_name || patient?.name
      ) === fullName
    );
    return exactNameMatches.length === 1 ? exactNameMatches[0] : null;
  };

  const syncRegistrationAfterAttach = async (response, patient) => {
    const token = localStorage.getItem('token');
    const incoming = getRegistrationDataForResponse(response);
    const current = {
      full_name: patient?.full_name || patient?.name || '',
      cpf: patient?.cpf || '',
      rg: patient?.rg || '',
      birth_date: patient?.birth_date || '',
      phone: patient?.phone || patient?.mobile_phone || patient?.whatsapp || '',
      email: patient?.email || '',
      address: patient?.address || ''
    };
    const fieldLabels = {
      full_name: 'Nome',
      cpf: 'CPF',
      rg: 'RG',
      birth_date: 'Data de nascimento',
      phone: 'Celular/telefone',
      email: 'E-mail',
      address: 'Endereço'
    };

    const blankFields = [];
    const conflictingFields = [];
    Object.entries(incoming).forEach(([field, value]) => {
      if (!value) return;
      const incomingValue = normalizeRegistrationValue(field, value);
      const currentValue = normalizeRegistrationValue(field, current[field]);
      if (!incomingValue || incomingValue === currentValue) return;
      if (!currentValue) blankFields.push([field, value]);
      else conflictingFields.push([field, value]);
    });

    const acceptedConflicts = [];
    conflictingFields.forEach(([field, value]) => {
      const shouldReplace = window.confirm(
        `${fieldLabels[field]} diferente no cadastro:\n\nAtual: ${current[field]}\nAnamnese: ${value}\n\nDeseja substituir somente este campo?\n\nOK = substituir\nCancelar = manter o atual e conservar o novo valor apenas na anamnese.`
      );
      if (shouldReplace) acceptedConflicts.push([field, value]);
    });

    const fieldsToUpdate = [...blankFields, ...acceptedConflicts];
    if (fieldsToUpdate.length === 0) return;

    const updateRes = await fetch(
      `${backendUrl}/api/anamnese/patients/${patient.id}/sync-registration`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(Object.fromEntries(fieldsToUpdate))
      }
    );
    if (!updateRes.ok) {
      const errorData = await updateRes.json().catch(() => ({}));
      throw new Error(
        errorData.detail || 'A anamnese foi anexada, mas o cadastro não pôde ser atualizado.'
      );
    }

    if (blankFields.length > 0 || acceptedConflicts.length > 0) {
      showToast('Cadastro atualizado com os dados confirmados da anamnese.', 'success');
    }
  };

  const attachResponseToSuggestedPatient = async (response, patient) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/anamnese/responses/${response.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: patient.id,
          template_id: response.template_id,
          answers: response.answers
        })
      });
      if (!res.ok) throw new Error('Erro ao vincular anamnese');

      try {
        await syncRegistrationAfterAttach(response, patient);
      } catch (syncError) {
        console.error('Erro ao sincronizar cadastro após vínculo:', syncError);
        showToast(
          syncError.message || 'Anamnese anexada, mas o cadastro não pôde ser atualizado.',
          'error'
        );
      }
      showToast(`Anamnese anexada ao paciente "${patient.full_name || patient.name}".`, 'success');
      await Promise.all([fetchResponses(), fetchKnownPatients()]);
      notifyPendingCountChanged();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao anexar anamnese', 'error');
    }
  };

  const handleKeepUnlinked = async (response) => {
    if (!window.confirm(
      `Manter a anamnese de "${response.patient_name}" somente nas respostas recebidas, sem criar ou anexar paciente?`
    )) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${backendUrl}/api/anamnese/responses/${response.id}/triage`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'kept_unlinked' })
        }
      );
      if (!res.ok) throw new Error('Erro ao atualizar situação da anamnese');

      showToast('Anamnese mantida sem vínculo com paciente.', 'success');
      await fetchResponses();
      notifyPendingCountChanged();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao atualizar anamnese', 'error');
    }
  };

  const handleUseTemplateAsBase = (template) => {
    const clonedQuestions = Array.isArray(template.questions)
      ? JSON.parse(JSON.stringify(template.questions))
      : [];

    setEditingTemplate({
      ...template,
      id: null,
      is_system: false,
      title: `${template.title || template.name || 'Template'} (cópia)`,
      name: `${template.name || template.title || 'Template'} (cópia)`,
      public: false,
      public_slug: null,
      public_link_id: null,
      questions: clonedQuestions.map((question, index) => ({
        ...question,
        id: `q${Date.now()}_${index + 1}`,
        order_index: index + 1,
      })),
    });

    setShowTemplateModal(true);
  };

  // Copiar link público
  const handleCopyPublicLink = async (template) => {
    // Verifica se já tem slug
    let publicLinkId = template.public_link_id || template.public_slug;
    
    // Se não tem slug, gera um tornando o template público
    if (!publicLinkId) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${backendUrl}/api/anamnese/templates/${template.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...template, public: true })
        });
        
        if (!res.ok) throw new Error('Erro ao gerar link');
        
        const updated = await res.json();
        publicLinkId = updated.public_link_id || updated.public_slug;
        
        // Atualiza a lista
        fetchTemplates();
      } catch (err) {
        console.error(err);
        showToast('Erro ao gerar link público', 'error');
        return;
      }
    }
    
    if (!publicLinkId) {
      showToast('Não foi possível gerar o link público', 'error');
      return;
    }
    
    const publicUrl = `${window.location.origin}/anamnese/publico/${publicLinkId}`;
    navigator.clipboard
      .writeText(publicUrl)
      .then(() => showToast('Link copiado para a área de transferência!', 'success'))
      .catch(() => showToast('Erro ao copiar link', 'error'));
  };

  // Submeter “anamnese local”
  const submitLocalAnamnese = async () => {
    if (!selectedTemplate) {
      showToast('Selecione um template para continuar', 'error');
      return;
    }
    if (!localAnamneseData.patient_name.trim()) {
      showToast('Nome do paciente é obrigatório', 'error');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const body = {
        patient_id: flowPatientId || undefined,
        template_id: selectedTemplate.id,
        patient_name: localAnamneseData.patient_name,
        patient_email: localAnamneseData.patient_email,
        patient_phone: localAnamneseData.patient_phone,
        patient_birth_date: localAnamneseData.patient_birth_date,
        answers: localAnamneseData.answers
      };
      const res = await fetch(`${backendUrl}/api/anamnese/local`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao criar anamnese');
      }
      showToast('Anamnese criada com sucesso', 'success');
      if (flowPatientId) {
        const registrationData = extractRegistrationData(
          selectedTemplate,
          localAnamneseData.answers
        );
        const currentRegistration = {
          cpf: flowPatientCpf,
          rg: flowPatientRg,
          birth_date: flowPatientBirthDate,
          phone: flowPatientPhone,
          email: flowPatientEmail,
          address: flowPatientAddress
        };
        const fieldLabels = {
          cpf: 'CPF',
          rg: 'RG',
          birth_date: 'Data de nascimento',
          phone: 'Celular/telefone',
          email: 'E-mail',
          address: 'Endereço'
        };
        const blankFields = [];
        const conflictingFields = [];
        Object.entries(registrationData).forEach(([field, value]) => {
          if (!value || !fieldLabels[field]) return;
          const currentValue = currentRegistration[field];
          if (
            normalizeRegistrationValue(field, value) ===
            normalizeRegistrationValue(field, currentValue)
          ) return;
          if (!String(currentValue || '').trim()) blankFields.push([field, value]);
          else conflictingFields.push([field, value]);
        });

        const acceptedConflicts = [];
        conflictingFields.forEach(([field, value]) => {
          const shouldReplace = window.confirm(
            `${fieldLabels[field]} diferente no cadastro:\n\nAtual: ${currentRegistration[field]}\nAnamnese: ${value}\n\nDeseja substituir somente este campo?\n\nOK = substituir\nCancelar = manter o atual e conservar o novo valor apenas na anamnese.`
          );
          if (shouldReplace) acceptedConflicts.push([field, value]);
        });

        const fieldsToUpdate = [...blankFields, ...acceptedConflicts];
        if (fieldsToUpdate.length > 0) {
            try {
              const updateBody = Object.fromEntries(fieldsToUpdate);
              const updateRes = await fetch(
                `${backendUrl}/api/anamnese/patients/${flowPatientId}/sync-registration`,
                {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(updateBody)
                }
              );

              if (!updateRes.ok) {
                const updateError = await updateRes.json().catch(() => ({}));
                throw new Error(
                  updateError.detail || 'Não foi possível atualizar o cadastro do paciente.'
                );
              }

              showToast(
                'Cadastro atualizado com os dados confirmados da anamnese',
                'success'
              );
            } catch (updateError) {
              console.error('Erro ao atualizar cadastro pela anamnese:', updateError);
              window.alert(
                `A anamnese foi salva, mas o cadastro do paciente não pôde ser atualizado.\n\n${updateError.message}`
              );
            }
        }

        window.location.href = `/prontuarios?patientId=${encodeURIComponent(
          flowPatientId
        )}&tab=anamnese`;
        return;
      }
      setLocalAnamneseData({
        patient_name: '',
        patient_email: '',
        patient_phone: '',
        patient_birth_date: '',
        answers: []
      });
      setSelectedTemplate(null);
      fetchResponses();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Erro ao criar anamnese', 'error');
    }
  };

  // Atualiza resposta local por questão
  const handleLocalAnswerChange = (questionId, value) => {
    setLocalAnamneseData((prev) => {
      const others = (prev.answers || []).filter((a) => a.question_id !== questionId);
      const question = (selectedTemplate?.questions || []).find((q) => q.id === questionId);
      const qText = question?.question_text || '';
      const lowerText = qText.toLowerCase();
      
      // Formatação automática baseada no texto da pergunta
      if (
        lowerText.includes('celular') ||
        lowerText.includes('whatsapp') ||
        lowerText === 'telefone' ||
        lowerText.startsWith('telefone ')
      ) {
        value = formatPhone(value);
      } else if (lowerText.includes('cpf')) {
        value = formatCPF(value);
      }
      
      return {
        ...prev,
        answers: [
          ...others,
          {
            question_id: questionId,
            question_text: qText,
            answer: value,
            answer_description: prev.answers.find((a) => a.question_id === questionId)?.answer_description || ''
          }
        ]
      };
    });
  };

  const handleLocalAnswerDescriptionChange = (questionId, description) => {
    setLocalAnamneseData((prev) => {
      const existing = (prev.answers || []).find((a) => a.question_id === questionId);
      const others = (prev.answers || []).filter((a) => a.question_id !== questionId);

      return {
        ...prev,
        answers: [
          ...others,
          {
            question_id: questionId,
            question_text: existing?.question_text || '',
            answer: existing?.answer || '',
            answer_description: description
          }
        ]
      };
    });
  };  

  // Safeguards para filtro e buscas
  const term = (searchTerm || '').toLowerCase();

  const filteredTemplates = (templates || []).filter((t) => {
    const title = (t?.title || '').toLowerCase();
    const description = (t?.description || '').toLowerCase();
    return title.includes(term) || description.includes(term);
  });

  const filteredResponses = (responses || []).filter((r) => {
    const name = (r?.patient_name || '').toLowerCase();
    const tmpl = (r?.template_title || '').toLowerCase();
    const email = (r?.patient_email || '').toLowerCase();
    return name.includes(term) || tmpl.includes(term) || email.includes(term);
  });

  const pendingResponses = (responses || []).filter(
    (response) => getResponseTriageStatus(response) === 'pending'
  );

  const responsesThisMonth = (responses || []).filter((r) => {
    if (!r?.submitted_at) return false;
    const d = new Date(r.submitted_at);
    if (isNaN(d)) return false;
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const isMobileLike =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    window.matchMedia('(max-width: 768px)').matches;

  const openWhatsAppWeb = async () => {
    if (isMobileLike) {
      showToast('No celular, abra o WhatsApp diretamente pelo aparelho para enviar o link da anamnese.', 'info');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      params.set('source', 'anamnese');

      const response = await fetch(`${backendUrl}/api/extension/session-ticket?${params.toString()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data?.ticket) {
        console.error('Erro ao gerar ticket da extensão:', data);
        showToast('Erro ao abrir integração com WhatsApp.', 'error');
        return;
      }

      const url = new URL('https://web.whatsapp.com');
      url.searchParams.set('odontoassist_source', 'anamnese');
      url.searchParams.set('ext_ticket', data.ticket);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Erro ao abrir integração com WhatsApp:', err);
      showToast('Erro ao abrir integração com WhatsApp.', 'error');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Anamnese Digital</h1>
            <p className="text-gray-600">
              Gerencie formulários de anamnese e colete informações dos pacientes de forma digital.
            </p>
          </div>

          {canSendWhatsApp && <button
            type="button"
            onClick={openWhatsAppWeb}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-60"
            disabled={loading}
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            Abrir WhatsApp Web
          </button>}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Templates Ativos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(templates || []).filter((t) => (t?.is_active ?? true)).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Respostas Recebidas</p>
                <p className="text-2xl font-bold text-gray-900">{(responses || []).length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Este Mês</p>
                <p className="text-2xl font-bold text-gray-900">{responsesThisMonth}</p>
              </div>
            </div>
          </div>
        </div>

        {pendingResponses.length > 0 && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900">
                  {pendingResponses.length === 1
                    ? '1 nova anamnese aguardando revisão'
                    : `${pendingResponses.length} novas anamneses aguardando revisão`}
                </p>
                <p className="text-sm text-amber-800 mt-1">
                  Decida se deseja criar um paciente, anexar a um cadastro existente ou manter somente a resposta.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActiveTab('responses')}
              className="shrink-0 px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 font-semibold text-sm"
            >
              Revisar agora
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Templates
            </button>
            <button
              onClick={() => setActiveTab('responses')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'responses'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Respostas Recebidas
              {pendingResponses.length > 0 && (
                <span className="ml-2 inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full bg-amber-500 text-white text-xs font-semibold">
                  {pendingResponses.length > 99 ? '99+' : pendingResponses.length}
                </span>
              )}
            </button>
            {canManage && <button
              data-onboarding-id="fill-anamnese"
              onClick={() => setActiveTab('local')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'local'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Edit3 className="w-4 h-4 inline mr-2" />
              Preencher Anamnese
            </button>}
          </nav>
        </div>

        {/* Busca + Ações */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={`Buscar ${activeTab === 'templates' ? 'templates' : 'respostas'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {activeTab === 'templates' && canManage && (
            <button
              onClick={() => setShowTemplateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 mt-4 sm:mt-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Template
            </button>
          )}
        </div>

        {/* Conteúdo principal */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Carregando...</p>
          </div>
        ) : activeTab === 'templates' ? (
          // TEMPLATES
          <div className="space-y-4">
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {term ? 'Nenhum template encontrado' : 'Nenhum template criado'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {term
                    ? 'Tente ajustar os filtros de busca.'
                    : 'Crie seu primeiro template de anamnese para começar a coletar informações dos pacientes.'}
                </p>
                {!term && canManage && (
                  <button
                    onClick={() => setShowTemplateModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeiro Template
                  </button>
                )}
              </div>
            ) : (
              filteredTemplates.map((template) => {
                const isActive = template?.is_active ?? true; // <- ajuste
                return (
                  <div key={template.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.title}</h3>
                        {template.is_default && (
                          <span className="inline-flex items-center mb-3 px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />
                            Anamnese padrão
                          </span>
                        )}
                        {template.description && (
                          <p className="text-gray-600 mb-3">{template.description}</p>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            {template?.questions?.length || 0} perguntas
                          </span>
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {template?.created_at
                              ? new Date(template.created_at).toLocaleDateString('pt-BR')
                              : '—'}
                          </span>
                          <span className={`flex items-center ${isActive ? 'text-green-600' : 'text-red-600'}`}>
                            {isActive ? (
                              <CheckCircle className="w-4 h-4 mr-1" />
                            ) : (
                              <AlertCircle className="w-4 h-4 mr-1" />
                            )}
                            {isActive ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {canManage && !template.is_default && (
                          <button
                            onClick={() => handleSetDefaultTemplate(template)}
                            className="px-3 py-2 text-sm text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                            title="Definir como Anamnese padrão"
                          >
                            Tornar padrão
                          </button>
                        )}
                        {canSend && <button
                          onClick={() => handleCopyPublicLink(template)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Copiar link público"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>}
                        {canManage && (template.is_system ? (
                          <button
                            onClick={() => handleUseTemplateAsBase(template)}
                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Usar como base"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingTemplate(template);
                                setShowTemplateModal(true);
                              }}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Editar"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>

                            <button
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              title="Deletar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : canManage && activeTab === 'local' ? (
          // PREENCHER LOCALMENTE
          <div className="space-y-6">
            {!selectedTemplate ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Selecione um Template</h3>
                <p className="text-gray-500 mb-4">
                  {flowPatientId
                    ? `Escolha o template para preencher a anamnese de ${flowPatientName || 'este paciente'}.`
                    : 'Escolha o template de anamnese que deseja preencher para um novo paciente.'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(templates || []).map((template) => (
                    <div
                      key={template.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <h4 className="font-medium text-gray-900 mb-2">{template.title}</h4>
                      <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <FileText className="w-4 h-4 mr-1" />
                        {template?.questions?.length || 0} perguntas
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">{selectedTemplate.title}</h3>
                  <button onClick={() => setSelectedTemplate(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Paciente *</label>
                  <input
                    type="text"
                    value={localAnamneseData.patient_name}
                    onChange={(e) => setLocalAnamneseData((p) => ({ ...p, patient_name: e.target.value }))}
                    readOnly={Boolean(flowPatientId)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nome completo do paciente"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {flowPatientId
                      ? 'Paciente selecionado automaticamente pelo prontuário'
                      : 'Os demais dados serão coletados nas perguntas abaixo'}
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <h4 className="font-medium text-gray-900">Perguntas da Anamnese</h4>
                  {(selectedTemplate?.questions || []).map((question, index) => {
                    const addressQuestion = isAddressQuestion(question);

                    return (
                      <div key={question.id || index} className="border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {index + 1}. {question?.question_text || 'Pergunta'}
                          {question?.is_required && <span className="text-red-500 ml-1">*</span>}
                        </label>

                        {question?.question_type === 'text' && (
                          addressQuestion ? (
                            <AddressCepField
                              value={
                                localAnamneseData.answers.find(
                                  (answer) => answer.question_id === question.id
                                )?.answer || ''
                              }
                              required={question.is_required}
                              onChange={(formattedAddress) =>
                                handleLocalAnswerChange(
                                  question.id,
                                  formattedAddress
                                )
                              }
                              inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <div>
                              <input
                                type="text"
                                value={
                                  localAnamneseData.answers.find(
                                    (answer) => answer.question_id === question.id
                                  )?.answer || ''
                                }
                                onChange={(e) =>
                                  handleLocalAnswerChange(
                                    question.id,
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={
                                  (question?.question_text || '')
                                    .toLowerCase()
                                    .includes('cpf')
                                    ? '000.000.000-00'
                                    : (
                                        (question?.question_text || '')
                                          .toLowerCase()
                                          .includes('telefone') ||
                                        (question?.question_text || '')
                                          .toLowerCase()
                                          .includes('celular') ||
                                        (question?.question_text || '')
                                          .toLowerCase()
                                          .includes('whatsapp')
                                      )
                                      ? '(00) 00000-0000'
                                      : 'Digite sua resposta...'
                                }
                              />
                            </div>
                          )
                        )}

                        {question?.question_type === 'textarea' && (
                          <textarea
                            onChange={(e) => handleLocalAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            rows={4}
                            placeholder="Digite sua resposta"
                          />
                        )}

                        {question?.question_type === 'radio' && (
                          <div className="space-y-2">
                            {(question?.options || []).map((option, optionIndex) => (
                              <label key={optionIndex} className="flex items-center">
                                <input
                                  type="radio"
                                  name={`question_${question.id}`}
                                  value={option}
                                  onChange={(e) => handleLocalAnswerChange(question.id, e.target.value)}
                                  className="mr-2"
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                        )}

                        {question?.allow_description && (
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {question?.description_label || 'Complemento da resposta'}
                            </label>
                            <textarea
                              value={
                                localAnamneseData.answers.find(a => a.question_id === question.id)?.answer_description || ''
                              }
                              onChange={(e) => handleLocalAnswerDescriptionChange(question.id, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows={3}
                              placeholder="Descreva, se necessário"
                            />
                          </div>
                        )}

                        {question?.question_type === 'date' && (
                          <input
                            type="date"
                            value={localAnamneseData.answers.find(a => a.question_id === question.id)?.answer || ''}
                            onChange={(e) => handleLocalAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        )}

                        {question?.question_type === 'checkbox' && (
                          <div className="space-y-2">
                            {(question?.options || []).map((option, optionIndex) => {
                              const currentValue =
                                localAnamneseData.answers.find(a => a.question_id === question.id)?.answer || [];

                              const selectedValues = Array.isArray(currentValue)
                                ? currentValue
                                : String(currentValue || '')
                                    .split(', ')
                                    .filter(Boolean);

                              return (
                                <label key={optionIndex} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedValues.includes(option)}
                                    onChange={(e) => {
                                      const nextValues = e.target.checked
                                        ? [...selectedValues, option]
                                        : selectedValues.filter((item) => item !== option);

                                      handleLocalAnswerChange(question.id, nextValues);
                                    }}
                                    className="mr-2"
                                  />
                                  {option}
                                </label>
                              );
                            })}
                          </div>
                        )}

                        {question?.question_type === 'select' && (
                          <select
                            value={localAnamneseData.answers.find(a => a.question_id === question.id)?.answer || ''}
                            onChange={(e) => handleLocalAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Selecione...</option>
                            {(question?.options || []).map((option, optionIndex) => (
                              <option key={optionIndex} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        )}

                        {question?.question_type === 'number' && (
                          <input
                            type="number"
                            value={localAnamneseData.answers.find(a => a.question_id === question.id)?.answer || ''}
                            onChange={(e) => handleLocalAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Digite um número"
                          />
                        )}

                        {question?.question_type === 'email' && (
                          <input
                            type="email"
                            value={localAnamneseData.answers.find(a => a.question_id === question.id)?.answer || ''}
                            onChange={(e) => handleLocalAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="email@exemplo.com"
                          />
                        )}

                        {question?.question_type === 'phone' && (
                          <input
                            type="text"
                            value={localAnamneseData.answers.find(a => a.question_id === question.id)?.answer || ''}
                            onChange={(e) => handleLocalAnswerChange(question.id, formatPhone(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="(00) 00000-0000"
                            maxLength={15}
                          />
                        )}

                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={submitLocalAnamnese}
                    disabled={!localAnamneseData.patient_name}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Salvar Anamnese
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // RESPOSTAS
          <div className="space-y-4">
            {filteredResponses.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {term ? 'Nenhuma resposta encontrada' : 'Nenhuma resposta recebida'}
                </h3>
                <p className="text-gray-500">
                  {term
                    ? 'Tente ajustar os filtros de busca.'
                    : 'Quando os pacientes preencherem seus formulários de anamnese, as respostas aparecerão aqui.'}
                </p>
              </div>
            ) : (
              filteredResponses.map((response) => {
                const triageStatus = getResponseTriageStatus(response);
                const isPending = triageStatus === 'pending';
                const isKeptUnlinked = triageStatus === 'kept_unlinked';
                const suggestedPatient = triageStatus !== 'linked'
                  ? getSuggestedPatientForResponse(response)
                  : null;
                return (
                <div
                  key={response.id}
                  className={`bg-white rounded-lg shadow p-6 ${isPending ? 'border-2 border-amber-300' : 'border border-transparent'}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {response?.patient_name || '—'}
                        </h3>
                        {isPending && (
                          <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                            Nova — aguardando decisão
                          </span>
                        )}
                        {triageStatus === 'linked' && (
                          <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                            Vinculada a paciente
                          </span>
                        )}
                        {isKeptUnlinked && (
                          <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                            Mantida sem vínculo
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        Template: {response?.template_title || '—'}
                      </p>
                      {suggestedPatient && (
                        <div className="mb-3 inline-flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
                          <span className="text-blue-900">
                            Possível cadastro existente: <strong>{suggestedPatient.full_name || suggestedPatient.name}</strong>
                          </span>
                          <button
                            type="button"
                            onClick={() => attachResponseToSuggestedPatient(response, suggestedPatient)}
                            className="font-semibold text-blue-700 hover:text-blue-900 underline"
                          >
                            Anexar a este paciente
                          </button>
                        </div>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {response?.submitted_at
                            ? `${new Date(response.submitted_at).toLocaleDateString('pt-BR')} às ${new Date(
                                response.submitted_at
                              ).toLocaleTimeString('pt-BR')}`
                            : '—'}
                        </span>
                        <span className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {response?.answers?.length || 0} respostas
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 lg:ml-4">
                      <button
                        onClick={() => {
                          setSelectedResponse(response);
                          setShowResponseModal(true);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium text-sm"
                        title="Visualizar"
                      >
                        <Eye className="w-4 h-4" /> Visualizar
                      </button>
                      {canCreatePatient && triageStatus !== 'linked' && <button
                        onClick={() => handleCreatePatient(response)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-sm"
                        title="Criar Paciente"
                      >
                        <UserPlus className="w-4 h-4" /> Criar paciente
                      </button>}
                      {canManage && triageStatus !== 'linked' && <button
                        onClick={() => handleAttachToPatient(response)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold text-sm"
                        title="Anexar a Paciente"
                      >
                        <Link className="w-4 h-4" /> Anexar a existente
                      </button>}
                      {canManage && isPending && <button
                        onClick={() => handleKeepUnlinked(response)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium text-sm"
                        title="Manter somente a resposta"
                      >
                        <FileText className="w-4 h-4" /> Manter sem vínculo
                      </button>}
                      {canManage && <button
                        onClick={() => handleDeleteResponse(response.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Deletar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>}
                    </div>
                  </div>
                </div>
              );})
            )}
          </div>
        )}

        {/* Template Modal */}
        {canManage && showTemplateModal && (
          <TemplateModal
            template={editingTemplate}
            onClose={() => {
              setShowTemplateModal(false);
              setEditingTemplate(null);
            }}
            onSave={() => {
              fetchTemplates();
              setShowTemplateModal(false);
              setEditingTemplate(null);
            }}
            showToast={showToast}
            backendUrl={backendUrl}
          />
        )}

        {/* Response Modal */}
        {showResponseModal && selectedResponse && (
          <ResponseModal
            response={selectedResponse}
            onClose={() => {
              setShowResponseModal(false);
              setSelectedResponse(null);
            }}
          />
        )}

        {/* Attach to Patient Modal */}
        {showAttachModal && selectedResponse && (
          <AttachPatientModal
            response={selectedResponse}
            onClose={() => {
              setShowAttachModal(false);
              setSelectedResponse(null);
            }}
            onAttach={async (patient) => {
              try {
                await syncRegistrationAfterAttach(selectedResponse, patient);
              } catch (syncError) {
                console.error('Erro ao sincronizar cadastro após vínculo:', syncError);
                showToast(
                  syncError.message || 'Anamnese anexada, mas o cadastro não pôde ser atualizado.',
                  'error'
                );
              }
              await Promise.all([fetchResponses(), fetchKnownPatients()]);
              notifyPendingCountChanged();
              setShowAttachModal(false);
              setSelectedResponse(null);
            }}
            showToast={showToast}
            backendUrl={backendUrl}
          />
        )}
      </div>
    </Layout>
  );
};

// ===================== Template Modal =====================
const TemplateModal = ({ template, onClose, onSave, showToast, backendUrl }) => {
  // Template padrão com CPF incluído
  const defaultQuestions = [
    { id: 'q1', question_text: 'Qual é o seu email?', question_type: 'text', options: [], is_required: true, order_index: 2 },
    { id: 'q2', question_text: 'Qual é a sua data de nascimento?', question_type: 'date', options: [], is_required: true, order_index: 3 },
    { id: 'q3', question_text: 'Qual é o seu telefone para contato?', question_type: 'text', options: [], is_required: true, order_index: 4 },
    { id: 'q4', question_text: 'Qual é o seu CPF?', question_type: 'text', options: [], is_required: true, order_index: 5 },
    { id: 'q5', question_text: 'Qual é o seu endereço completo (com CEP)?', question_type: 'text', options: [], is_required: false, order_index: 6 },
    { id: 'q6', question_text: 'Você tem alguma alergia conhecida?', question_type: 'radio', options: ['Sim', 'Não', 'Não sei'], is_required: true, order_index: 7 },
    { id: 'q7', question_text: 'Se sim, especifique quais alergias você tem:', question_type: 'textarea', options: [], is_required: false, order_index: 8 },
    { id: 'q8', question_text: 'Você toma algum medicamento regularmente?', question_type: 'radio', options: ['Sim', 'Não'], is_required: true, order_index: 9 },
    { id: 'q9', question_text: 'Se sim, quais medicamentos você toma?', question_type: 'textarea', options: [], is_required: false, order_index: 10 },
    { id: 'q10', question_text: 'Você tem algum problema de saúde?', question_type: 'radio', options: ['Sim', 'Não', 'Não sei'], is_required: true, order_index: 11 },
    { id: 'q11', question_text: 'Se sim, descreva os problemas de saúde:', question_type: 'textarea', options: [], is_required: false, order_index: 12 },
    { id: 'q12', question_text: 'Você já fez algum tratamento dentário anterior?', question_type: 'radio', options: ['Sim', 'Não', 'Não lembro'], is_required: true, order_index: 13 },
    { id: 'q13', question_text: 'Você sente dor ou desconforto nos dentes atualmente?', question_type: 'radio', options: ['Sim', 'Não', 'Às vezes'], is_required: true, order_index: 14 },
    { id: 'q14', question_text: 'Se sim, descreva a dor ou desconforto:', question_type: 'textarea', options: [], is_required: false, order_index: 15 },
    { id: 'q15', question_text: 'Você escova os dentes quantas vezes por dia?', question_type: 'radio', options: ['1 vez', '2 vezes', '3 vezes ou mais', 'Raramente'], is_required: true, order_index: 16 },
    { id: 'q16', question_text: 'Você usa fio dental regularmente?', question_type: 'radio', options: ['Sim, diariamente', 'Sim, ocasionalmente', 'Raramente', 'Nunca'], is_required: true, order_index: 17 },
    { id: 'q17', question_text: 'Há algo mais que você gostaria de nos informar sobre sua saúde bucal?', question_type: 'textarea', options: [], is_required: false, order_index: 18 }
  ];

  const [formData, setFormData] = useState({
    title: template?.title || '',
    description: template?.description || '',
    public: template?.public || false,
    questions: template?.questions || defaultQuestions
  });
  const [loading, setLoading] = useState(false);
  const initialFormRef = useRef(formData);
  const overlayMouseDownRef = useRef(false);

  const [optionsDrafts, setOptionsDrafts] = useState({});

  const requestClose = () => {
    const isDirty = hasFormChanged(initialFormRef.current, formData);
    if (confirmDiscardChanges(isDirty)) onClose();
  };

  const handleOverlayMouseDown = (event) => {
    overlayMouseDownRef.current = event.target === event.currentTarget;
  };

  const handleOverlayMouseUp = (event) => {
    if (overlayMouseDownRef.current && event.target === event.currentTarget) requestClose();
    overlayMouseDownRef.current = false;
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      question_text: '',
      question_type: 'text',
      options: [],
      is_required: true,
      order_index: (formData?.questions?.length || 0) + 1
    };
    setFormData((prev) => ({ ...prev, questions: [...(prev.questions || []), newQuestion] }));
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...(formData.questions || [])];
    updated[index] = { ...updated[index], [field]: value };
    setFormData((prev) => ({ ...prev, questions: updated }));
  };

  const removeQuestion = (index) => {
    const updated = (formData.questions || []).filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, questions: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showToast('Título do template é obrigatório', 'error');
      return;
    }
    if ((formData.questions || []).length === 0) {
      showToast('Adicione pelo menos uma pergunta', 'error');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const isNewFromBase = !template?.id;

      const method = template && !isNewFromBase ? 'PUT' : 'POST';

      const url = template && !isNewFromBase
        ? `${backendUrl}/api/anamnese/templates/${template.id}`
        : `${backendUrl}/api/anamnese/templates`;

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Erro ao salvar template');
      showToast(template ? 'Template atualizado com sucesso!' : 'Template criado com sucesso!', 'success');
      onSave();
    } catch (err) {
      console.error(err);
      showToast('Erro ao salvar template', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{template ? 'Editar Template' : 'Novo Template'}</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título do Template *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Anamnese Odontológica Completa"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descrição opcional do template..."
            />
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.public}
                onChange={(e) => setFormData((p) => ({ ...p, public: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Tornar público (gerar link para compartilhar)
              </span>
            </label>
            {formData.public && (
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Um link único será gerado para que pacientes possam preencher este formulário
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">Perguntas ({formData?.questions?.length || 0})</label>
            </div>

            <div className="space-y-4">
              {(formData?.questions || []).map((question, index) => (
                <div key={question.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-sm font-medium text-gray-500">Pergunta {index + 1}</span>
                    <button type="button" onClick={() => removeQuestion(index)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        placeholder="Digite sua pergunta..."
                        value={question?.question_text || ''}
                        onChange={(e) => updateQuestion(index, 'question_text', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <select
                          value={question?.question_type || 'text'}
                          onChange={(e) => updateQuestion(index, 'question_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="text">Texto</option>
                          <option value="textarea">Texto Longo</option>
                          <option value="radio">Múltipla Escolha</option>
                          <option value="checkbox">Seleção Múltipla</option>
                          <option value="select">Lista Suspensa</option>
                          <option value="number">Número</option>
                          <option value="date">Data</option>
                          <option value="email">E-mail</option>
                          <option value="phone">Telefone</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={!!question?.is_required}
                            onChange={(e) => updateQuestion(index, 'is_required', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-600">Obrigatória</span>
                        </label>
                      </div>
                    </div>

                    {(question?.question_type === 'radio' ||
                      question?.question_type === 'checkbox' ||
                      question?.question_type === 'select') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Opções
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Digite uma opção por linha. Exemplo: Sim, Não, Não sei.
                        </p>
                        <textarea
                          placeholder={"Sim\nNão\nNão sei"}
                          value={
                            optionsDrafts[question.id] !== undefined
                              ? optionsDrafts[question.id]
                              : (question?.options || []).join('\n')
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value;

                            setOptionsDrafts((prev) => ({
                              ...prev,
                              [question.id]: rawValue,
                            }));

                            updateQuestion(
                              index,
                              'options',
                              rawValue
                                .split('\n')
                                .map((s) => s.trim())
                                .filter(Boolean)
                            );
                          }}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <button
                  type="button"
                  onClick={addQuestion}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Pergunta
                </button>
              </div>
            </div>

            {(formData?.questions || []).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>Nenhuma pergunta adicionada ainda.</p>
                <p className="text-sm">Clique em "Adicionar Pergunta" para começar.</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <button type="button" onClick={requestClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : template ? 'Atualizar' : 'Criar Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===================== Response Modal =====================
const ResponseModal = ({ response, onClose }) => {
  console.log('🔍 ResponseModal - response recebida:', {
    id: response?.id,
    patient_name: response?.patient_name,
    has_answers_resolved: !!response?.answers_resolved,
    answers_resolved_length: response?.answers_resolved?.length,
    has_answers: !!response?.answers,
    answers_type: Array.isArray(response?.answers) ? 'array' : typeof response?.answers,
    first_answer_resolved: response?.answers_resolved?.[0]
  });
  
  // Normaliza answers para sempre ser um array
  const getAnswersArray = () => {
    if (!response) return [];
    
    // Se tem answers_resolved (vem do backend com resolve_labels=true)
    if (response.answers_resolved && Array.isArray(response.answers_resolved)) {
      console.log('✓ Usando answers_resolved com', response.answers_resolved.length, 'itens');
      return response.answers_resolved;
    }
    
    // Se tem answers
    if (!response.answers) return [];
    
    // Se já é array, retorna direto
    if (Array.isArray(response.answers)) {
      console.log('✓ Usando answers (array) com', response.answers.length, 'itens');
      return response.answers;
    }
    
    // Se é objeto (dict), converte para array
    if (typeof response.answers === 'object') {
      const arr = Object.entries(response.answers).map(([key, value]) => ({
        question_id: key,
        question_text: key,
        key: key,
        label: key,
        answer: value,
        value: value
      }));
      console.log('✓ Convertendo answers (dict) para array com', arr.length, 'itens');
      return arr;
    }
    
    return [];
  };
  
  const answersArray = getAnswersArray();
  console.log('🎯 answersArray final:', answersArray.length, 'itens', answersArray[0]);
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{response?.patient_name || '—'}</h2>
              <p className="text-gray-600">Template: {response?.template_title || '—'}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Informações do Paciente</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p>
                <span className="font-medium">Nome:</span> {response?.patient_name || '—'}
              </p>
              {response?.patient_email && (
                <p>
                  <span className="font-medium">Email:</span> {response.patient_email}
                </p>
              )}
              <p>
                <span className="font-medium">Data de Submissão:</span>{' '}
                {response?.submitted_at ? new Date(response.submitted_at).toLocaleString('pt-BR') : 
                 response?.created_at ? new Date(response.created_at).toLocaleString('pt-BR') : '—'}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-4">Respostas ({answersArray.length})</h3>
            {answersArray.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nenhuma resposta registrada</p>
            ) : (
            <div className="space-y-4">
              {answersArray.map((answer, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">
                    {answer?.label || answer?.question_text || answer?.question_id || `Pergunta ${index + 1}`}
                  </h4>
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {answer?.value || answer?.answer || answer?.response || '—'}
                  </p>
                </div>
              ))}
            </div>
            )}
          </div>

          <div className="flex justify-end pt-6 border-t mt-6">
            <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== Attach Patient Modal =====================
const AttachPatientModal = ({ response, onClose, onAttach, showToast, backendUrl }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${backendUrl}/api/patients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Erro ao buscar pacientes');
        const data = await res.json();
        setPatients(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        showToast('Erro ao carregar lista de pacientes', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, [backendUrl, showToast]);

  const handleAttach = async () => {
    if (!selectedPatientId) {
      showToast('Selecione um paciente', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/anamnese/responses/${response.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: selectedPatientId,
          template_id: response.template_id,
          answers: response.answers
        })
      });

      if (!res.ok) throw new Error('Erro ao vincular anamnese');

      const selectedPatient = patients.find(p => p.id === selectedPatientId);
      showToast(`Anamnese anexada ao paciente "${selectedPatient?.full_name || selectedPatient?.name}"!`, 'success');
      await onAttach(selectedPatient);
    } catch (err) {
      console.error(err);
      showToast('Erro ao anexar anamnese', 'error');
    }
  };

  const filteredPatients = patients.filter(p => {
    const name = (p.full_name || p.name || '').toLowerCase();
    const email = (p.email || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Anexar a Paciente</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Anamnese de: <strong>{response.patient_name}</strong>
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Carregando pacientes...</p>
            </div>
          ) : (
            <>
              {/* Campo de Busca */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar Paciente
                </label>
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Digite o nome ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Lista de Pacientes */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione o Paciente ({filteredPatients.length})
                </label>
                <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                  {filteredPatients.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      Nenhum paciente encontrado
                    </div>
                  ) : (
                    filteredPatients.map((patient) => (
                      <label
                        key={patient.id}
                        className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                          selectedPatientId === patient.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="patient"
                          value={patient.id}
                          checked={selectedPatientId === patient.id}
                          onChange={(e) => setSelectedPatientId(e.target.value)}
                          className="mr-3 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {patient.full_name || patient.name}
                          </p>
                          {patient.email && (
                            <p className="text-sm text-gray-500">{patient.email}</p>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleAttach}
            disabled={!selectedPatientId || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anexar Anamnese
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnamnesePage;
